import {
  BeakerIcon,
  BuildingOfficeIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  EyeDropperIcon,
  FaceFrownIcon,
  HeartIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContactRow } from "../../api/relationship.type";
import { UserRow } from "../../api/user.type";
import DiagnosesPage from "../../pages/diagnoses-page";
import SymptomsPage from "../../pages/symptoms-page";
import MedicationsPage from "../../pages/medications-page";
import ProvidersPage from "../../pages/providers-page";
import TestsPage from "../../pages/tests-page";
import VisitsPage from "../../pages/visits-page";
import VitalsPage from "../../pages/vitals-page";
import Card from "../shared/card";
import { SubmitButton } from "../shared/buttons/submit-button";
import ThemedSpinner from "../shared/spinner";
import Typography from "../shared/typography";
import Avatar from "../shared/avatar";
import { ContactRecordsPickerModal } from "../medical-records/contact-records-picker-modal";

type Tab =
  | "symptom"
  | "diagnosis"
  | "medication"
  | "provider"
  | "test"
  | "vital"
  | "visit";

const tabs: { id: Tab; label: string }[] = [
  { id: "symptom", label: "Symptoms" },
  { id: "diagnosis", label: "Diagnoses" },
  { id: "medication", label: "Medications" },
  { id: "provider", label: "Providers" },
  { id: "test", label: "Tests" },
  { id: "vital", label: "Vitals" },
  { id: "visit", label: "Visits" },
];

const tabIcons: Record<Tab, React.ComponentType<{ className?: string }>> = {
  symptom: FaceFrownIcon,
  diagnosis: ClipboardDocumentCheckIcon,
  medication: EyeDropperIcon,
  provider: UserGroupIcon,
  test: BeakerIcon,
  vital: HeartIcon,
  visit: BuildingOfficeIcon,
};

type MedicalRecordsLayoutProps = {
  user: UserRow | null;
  contacts: ContactRow[];
  contactsLoading: boolean;
};

function MedicalRecordsLayout({
  user,
  contacts,
  contactsLoading,
}: MedicalRecordsLayoutProps) {
  const navigate = useNavigate();
  const { userId, section } = useParams<{
    userId?: string;
    section?: string;
  }>();
  const initialTab =
    section && tabs.some((t) => t.id === section)
      ? (section as Tab)
      : "diagnosis";
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);

  const contactsWithRecordAccess = useMemo(
    () => contacts.filter((contact) => contact.record_view),
    [contacts]
  );

  const activeContact = userId
    ? contacts.find((contact) => contact.related_user_id === userId)
    : null;

  const contactDisplayName = activeContact?.related_user
    ? `${activeContact.related_user.first_name ?? ""} ${
        activeContact.related_user.last_name ?? ""
      }`.trim() ||
      activeContact.related_user.email ||
      "Contact"
    : null;

  const contactAvatar =
    activeContact?.photo_url || activeContact?.related_user?.avatar_url;

  const headerTitle = contactDisplayName
    ? `${contactDisplayName}'s Medical Records`
    : user
    ? `${user.first_name} ${user.last_name}'s Medical Records`
    : "Medical Records";

  const canViewRecords = activeContact ? Boolean(activeContact.record_view) : true;
  const contactNotFound = Boolean(userId && !contactsLoading && !activeContact);

  useEffect(() => {
    const newTab =
      section && tabs.some((t) => t.id === section)
        ? (section as Tab)
        : "diagnosis";
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // Render the appropriate section based on active tab
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case "diagnosis":
        return <DiagnosesPage />;
      case "symptom":
        return <SymptomsPage />;
      case "medication":
        return <MedicationsPage />;
      case "provider":
        return <ProvidersPage />;
      case "test":
        return <TestsPage />;
      case "vital":
        return <VitalsPage />;
      case "visit":
        return <VisitsPage />;
      default:
        return <div>Diagnoses</div>;
    }
  };

  const buildSectionPath = (tab: Tab, overrideUserId?: string) => {
    const destinationUserId = overrideUserId ?? userId;
    return destinationUserId
      ? `/medical-records/user/${destinationUserId}/section/${tab}`
      : `/medical-records/section/${tab}`;
  };

  const handleCreateRecord = () => {
    const path = buildSectionPath(activeTab);
    navigate(`${path}?mode=create`);
  };

  const handleRequestRecords = () => {
    navigate("/medical-records/requests");
  };

  const handleSelectContact = (contact: ContactRow) => {
    setIsContactPickerOpen(false);
    if (!contact.related_user_id) {
      return;
    }
    const destination = buildSectionPath(activeTab, contact.related_user_id);
    navigate(destination);
  };

  return (
    <div className="sticky top-8 w-full">
      <div className="bg-white rounded-lg shadow-sm max-w-6xl mx-auto">
        <div className="p-6 border-b flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            {contactAvatar && (
              <Avatar
                src={contactAvatar}
                fallbackText={contactDisplayName ?? headerTitle}
                size="lg"
                className="hidden sm:block"
              />
            )}
            <div>
              <h2 className="text-2xl font-bold text-terracotta-500">
                {headerTitle}
              </h2>
              {contactDisplayName && (
                <Typography variant="body2" className="text-brown-500">
                  Viewing records for {contactDisplayName}
                </Typography>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SubmitButton
              type="button"
              size="sm"
              color="terracotta"
              hideArrow
              icon={<PlusIcon className="w-4 h-4" />}
              onClick={handleCreateRecord}
              isDisabled={!canViewRecords}
            >
              Create
            </SubmitButton>
            <SubmitButton
              type="button"
              size="sm"
              color="brown"
              hideArrow
              icon={<ClipboardDocumentListIcon className="w-4 h-4" />}
              onClick={handleRequestRecords}
            >
              Request
            </SubmitButton>
            <SubmitButton
              type="button"
              size="sm"
              color="cream"
              iconOnly
              hideArrow
              aria-label="Switch contact"
              onClick={() => setIsContactPickerOpen(true)}
              icon={<UserGroupIcon className="w-4 h-4 text-brown-800" />}
            />
          </div>
        </div>
        <div className="p-6 border-b">
          <div className="flex flex-wrap gap-4 lg:flex-nowrap lg:justify-between">
            {tabs.map((tab) => {
              const Icon = tabIcons[tab.id];
              const isActive = activeTab === tab.id;
              return (
                <Card
                  key={tab.id}
                  onClick={() => {
                    const path = buildSectionPath(tab.id);
                    setActiveTab(tab.id);
                    navigate(path);
                  }}
                  hoverEffect
                  variant={isActive ? "terracotta" : "default"}
                  className="flex flex-col items-center justify-center p-3 cursor-pointer flex-1 min-w-[140px] sm:min-w-[160px] lg:min-w-0"
                >
                  <Icon
                    className={`w-6 h-6 ${
                      isActive ? "text-cream-500" : "text-terracotta-500"
                    }`}
                  />
                  <Typography
                    variant="caption"
                    className={`mt-2 ${
                      isActive ? "text-white" : "text-terracotta-500"
                    }`}
                  >
                    {tab.label}
                  </Typography>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {contactsLoading && userId && !activeContact ? (
            <div className="flex items-center justify-center py-10">
              <ThemedSpinner />
            </div>
          ) : contactNotFound ? (
            <Card variant="cream" className="text-center" elevation={1}>
              <Card.Body>
                <Typography variant="h4" className="text-terracotta">
                  Contact not found
                </Typography>
                <Typography variant="body2" className="mt-2 text-brown-700">
                  We couldn’t find this contact in your relationships. Try
                  selecting another contact from the picker.
                </Typography>
              </Card.Body>
            </Card>
          ) : !canViewRecords ? (
            <Card variant="cream" className="space-y-3" elevation={1}>
              <Card.Body>
                <Typography variant="h4" className="text-terracotta">
                  Access required
                </Typography>
                <Typography variant="body2" className="text-brown-700">
                  {contactDisplayName || "This contact"} hasn’t shared their
                  medical records with you yet. Request access to review their
                  diagnoses, medications, and visits.
                </Typography>
                <SubmitButton
                  type="button"
                  size="sm"
                  color="brown"
                  onClick={handleRequestRecords}
                >
                  Request Access
                </SubmitButton>
              </Card.Body>
            </Card>
          ) : (
            renderActiveTabContent()
          )}
        </div>
      </div>

      <ContactRecordsPickerModal
        contacts={contactsWithRecordAccess}
        isLoading={contactsLoading}
        isOpen={isContactPickerOpen}
        onClose={() => setIsContactPickerOpen(false)}
        onSelect={handleSelectContact}
      />
    </div>
  );
}

export default MedicalRecordsLayout;
